import * as THREE from "three";
import { Cell, Heading } from "@highmarch/domain";
import { hash2, safeNormals, smoothstepN, strata, vnoise } from "./groundNoise";
import { GroundGeometry } from "./groundGeometry";

/**
 * The gorge walls (groundMesh.ts's second layer, over groundGeometry.ts): a cliff band down every edge where the ground
 * meets a chasm or a lava basin, with ledges, fractured columns and a batter, and the scree spots along its lip and foot.
 */
export class GroundCliffs extends GroundGeometry {
  /**
   * World units per texture repeat on a cliff face. Denser than the ground's — a gorge wall is only a couple of
   * units tall, so at the ground's one-repeat-per-four-units it showed half a repeat and read as three enormous flat
   * slabs. Not too dense either: at 1.35 the marsh's bedded stone tiled into regular masonry and the gorge looked
   * like a stone-lined canal.
   */
  static readonly CliffScale = 2.4;
  /** How far a cliff strip runs past its cell edge, so perpendicular faces close the corner instead of leaving a slot. */
  static readonly Overhang = 0.1;
  /** How far the cliff's top row sits under the ground surface, and in under its edge (the grass overhangs it). */
  static readonly LipDrop = 0.01;
  static readonly LipTuck = 0.02;
  /** Horizontal depth of the gorge wall's stacked-stone ledges, per quantised step (four steps). */
  static readonly Ledge = 0.12;
  /**
   * The wall is also broken ALONG its length into fractured columns, each standing a little proud of or behind its
   * neighbours (± half this, in world units). Ledges alone are horizontal lines; horizontal lines round a trench are
   * courses, and courses read as a stone-lined canal (the open bug). Columns every ~1/ColumnFreq units.
   */
  static readonly ColumnJog = 0.11;
  static readonly ColumnFreq = 2.3;
  /** Horizontal segments per unit of wall: more than the surface's `Sub` so the column steps have vertices to land on. */
  static readonly CliffSub = 8;
  /** Scree spots per wall edge, at the lip and at the foot. */
  static readonly ScreePerEdge = 3;
  /**
   * Where the gorge walls meet the ground above (lip) and the floor below (foot), spaced along every wall edge, for
   * the half-buried scree that breaks both lines (DetailScatter.buildScree, Phase 3 §6). Filled by `buildCliffs`.
   * `nx, nz` is the wall's outward direction (into the chasm); `lava` marks a basin wall (no scree at its foot).
   */
  static currentGorge: GorgeSpot[] = [];
  /** Bottom of the cliff below a cell edge: the molten surface, or the chasm floor. */
  protected floorBeside(n: Cell): number {
    return this.terrain.isLava(n) ? this.terrain.lavaAt(n.x, n.y) - 0.16 : this.terrain.floorAt(n.x, n.y);
  }

  /**
   * Cliff faces. There are only two kinds left: the island's own rim, and the wall around a chasm. Height differences
   * between neighbouring cells no longer produce one, because the ground no longer steps — that is what removed the
   * terraces. Each face's top follows the rolling surface along the edge rather than sitting at one level.
   */
  protected buildCliffs(): THREE.BufferGeometry {
    const { grid, terrain } = this;
    const N = GroundCliffs.CliffSub;                             // horizontal segments per edge
    const pos: number[] = [], uv: number[] = [], idx: number[] = [], tops: number[] = [];
    const spots: GorgeSpot[] = [];
    const dirs: [Heading, number, number][] = [[Heading.North, 0, 1], [Heading.East, 1, 0], [Heading.South, 0, -1], [Heading.West, -1, 0]];
    for (const c of this.landCells()) {
      for (const [, dx, dz] of dirs) {
        const n = new Cell(c.x + dx, c.y + dz);
        if (this.solid(n)) continue;                           // ground meets ground: a slope, never a wall
        // The only walls are round the holes INSIDE the map. The land past the map is `Beyond` cells deep and then
        // simply stops, and its outermost ring has no neighbour either — so this used to hang a 2.3-unit wall off
        // the far edge of the apron all the way round. Its inward face (the material is double-sided, and had no
        // haze) rendered as the unlit, unfogged black rectangle standing at the map edge in several camera yaws.
        if (!this.grid.inBounds(n)) continue;
        const bottom = this.floorBeside(n);
        // the edge runs perpendicular to (dx, dz); walk it in world space
        const ex = dz, ez = -dx;                               // along-edge unit
        const cx = c.x + dx * 0.5, cz = c.y + dz * 0.5;        // edge centre
        const top = this.heightIn(cx, cz);
        const rows = Math.max(3, Math.round((top - bottom) * 5));
        const base = pos.length / 3;
        for (let r = 0; r <= rows; r++) {
          const t = r / rows;
          for (let s = 0; s <= N; s++) {
            const a = -0.5 - GroundCliffs.Overhang + (1 + GroundCliffs.Overhang * 2) * (s / N);
            let wx = cx + ex * a, wz = cz + ez * a;
            // The lip tracks the rolling ground along this edge, so the cliff top rises and falls with the meadow.
            // It sits a centimetre UNDER the ground and a little in under its edge, so the grass overhangs the wall.
            // It used to stand 0.055 proud and 0.055 out: a thin up-facing strip that took the grass colour and the
            // full sun, and drew a white hairline round every gorge (terrain F8).
            const lip = this.heightIn(wx, wz) - GroundCliffs.LipDrop;
            const y = lip - (lip - bottom) * t;
            const [dwx, dwz] = this.warpAt(wx, wz);            // same wander as the surface, so the lip follows it
            wx += dwx; wz += dwz;
            // The wall leans in as it goes down — a batter. A dead-vertical face all the way to a flat floor is a
            // trench; real ground sheds into its own gorge and the bottom is narrower than the lip.
            const batter = t * t * 0.30;
            // Stacked-stone ledges: the strata displacement is quantised into four steps, so the wall reads as a few
            // chunky shelves (with faceted shading, see cliffMaterial) instead of one planar sheet with a gentle wave.
            // Fractured columns across the ledges: which column this vertex is in comes from the world position along
            // the wall (so faces that meet at a corner agree), wandering with height so the joints are not plumb lines.
            const along = (cx + ex * a) * ex + (cz + ez * a) * ez;
            const colJ = Math.floor(along * GroundCliffs.ColumnFreq + (vnoise(along * 0.9 + 5.1, y * 1.3) - 0.5) * 1.4);
            const colOff = (hash2(colJ * 1.7 + dx * 3.1, dz * 5.3 + 0.7) - 0.5) * GroundCliffs.ColumnJog;
            const bulge = t < 0.06 ? -GroundCliffs.LipTuck
              : Math.floor(strata(y, wx + wz) * 4) / 4 * GroundCliffs.Ledge + 0.02 + colOff * smoothstepN(0.06, 0.2, t);
            wx += dx * (bulge + batter); wz += dz * (bulge + batter);
            pos.push(wx, y, wz);
            // scree: a few spots per edge at the lip row and the foot row (the wall's own warped, battered position)
            if ((r === 0 || r === rows) && s > 0 && s < N && s % Math.round(N / GroundCliffs.ScreePerEdge) === 1) {
              const lip = r === 0, lava = this.terrain.isLava(n);
              if (lip || !lava) spots.push({ x: wx + dx * (lip ? -0.04 : 0.1), y: lip ? y + GroundCliffs.LipDrop : y, z: wz + dz * (lip ? -0.04 : 0.1), nx: dx, nz: dz, lip, lava, h: hash2(wx * 3.7, wz * 5.1) });
            }
            // offset per edge so neighbouring faces do not line their courses up into one continuous wall
            const jog = ((c.x * 7 + c.y * 13) % 5) * 0.23;
            uv.push((a + (c.x * ex + c.y * ez)) / GroundCliffs.CliffScale + jog, y / GroundCliffs.CliffScale);
            tops.push(lip);
          }
        }
        for (let r = 0; r < rows; r++) {
          for (let s = 0; s < N; s++) {
            const a = base + r * (N + 1) + s, b = a + 1, d = a + (N + 1), e = d + 1;
            // wind the quad so the face points away from the cell
            if (dx + dz > 0) idx.push(a, b, d, b, e, d);
            else idx.push(a, d, b, b, d, e);
          }
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    g.setAttribute("cliffTop", new THREE.Float32BufferAttribute(tops, 1));
    g.setIndex(idx);
    safeNormals(g);
    GroundCliffs.currentGorge = spots;
    return g;
  }
}

/** A scree spot on a gorge wall's lip or foot (see `GroundMesh.currentGorge`). */
export interface GorgeSpot { x: number; y: number; z: number; nx: number; nz: number; lip: boolean; lava: boolean; h: number }
